import {
    createTheme as createMuiTheme,
    responsiveFontSizes
} from '@mui/material/styles'
import { createOptions as createBaseOptions } from './base/create-options'
import { createOptions as createBlackOptions } from './black/create-options'
import { createOptions as createBlackExperimentalOptions } from './black-experimental/create-options'
import { createOptions as createDarkOptions } from './dark/create-options'
import { createOptions as createGreyOptions } from './grey/create-options'
import { createOptions as createPastelOptions } from './pastel/create-options'
import { createOptions as createWhiteOptions } from './white/create-options'

// eslint-disable-next-line consistent-return
const getTheme = (config) => {
    if (config.theme === 'white') {
        return createWhiteOptions({
            colorPreset: config.colorPreset,
            contrast: config.contrast
        })
    }
    if (config.theme === 'pastel') {
        return createPastelOptions({
            colorPreset: config.colorPreset,
            contrast: config.contrast
        })
    }
    if (config.theme === 'grey') {
        return createGreyOptions({
            colorPreset: config.colorPreset,
            contrast: config.contrast
        })
    }
    if (config.theme === 'dark') {
        return createDarkOptions({
            colorPreset: config.colorPreset,
            contrast: config.contrast
        })
    }
    if (config.theme === 'black') {
        return createBlackOptions({
            colorPreset: config.colorPreset,
            contrast: config.contrast
        })
    }
    if (config.theme === 'black-experimental') {
        return createBlackExperimentalOptions({
            colorPreset: config.colorPreset,
            contrast: config.contrast
        })
    }
}

// White-label palette override (partner branding). Applied AFTER the preset
// resolves so it wins regardless of the user's theme/colorPreset, and run
// through augmentColor so light/dark/contrastText are derived from the brand
// main rather than leaking through from the preset's hand-tuned shades.
// Callers validate the hex — anything falsy is simply skipped.
const applyBrandPalette = (theme, config) => {
    if (
        !config.brandPrimary &&
        !config.brandSecondary &&
        !config.fallbackSecondary
    ) {
        return theme
    }
    const palette = {}
    if (config.brandPrimary) {
        palette.primary = theme.palette.augmentColor({
            color: { main: config.brandPrimary }
        })
    }
    if (config.brandSecondary) {
        palette.secondary = theme.palette.augmentColor({
            color: { main: config.brandSecondary }
        })
    } else if (config.fallbackSecondary) {
        // A full { main, dark, contrastText } set: augmentColor keeps the given shades.
        palette.secondary = theme.palette.augmentColor({
            color: { ...config.fallbackSecondary }
        })
    }
    return createMuiTheme(theme, { palette })
}

// Platform brand typeface (theme/brands.js): headings only, so body text and
// data stay in the app's UI font whichever domain the visitor is on.
const HEADINGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']
const applyBrandTypography = (theme, config) => {
    if (!config.brandHeadingFont) return theme
    const typography = Object.fromEntries(
        HEADINGS.map((variant) => [
            variant,
            { fontFamily: config.brandHeadingFont }
        ])
    )
    return createMuiTheme(theme, { typography })
}

export const createTheme = (config) => {
    let theme = createMuiTheme(
        createBaseOptions({
            direction: config.direction,
            roundedCorners: config.roundedCorners
        }),

        getTheme(config)
    )

    theme = applyBrandPalette(theme, config)
    theme = applyBrandTypography(theme, config)

    if (config.responsiveFontSizes) {
        theme = responsiveFontSizes(theme)
    }

    return theme
}
